import { useState, useRef, useMemo, useCallback } from 'react'
import { Container, HeaderBar, TypoGraph } from 'components'
import styles from './summaryReport.module.scss'
import strings from 'strings'
import { useGridApiRef } from '@mui/x-data-grid'
import { Chip } from '@mui/material'
import ScrollToTopCornerButton from 'components/scrollToTopButton'
import CarrierHeaderKPIs from './partial/headerKPIs/index'
import DataCoverage from './partial/dataCoverage'
import SummaryRadiusOfOperation from './partial/summaryRadiusOfOperation'
import ActiveUnits from './partial/activeUnits'
import EstimatedMiles from './partial/estimatedMilesTotal'
import PdfExport from 'components/pdfExport'
import { PdfExportSection } from 'interfaces/ISummaryReport'

const t = strings.CARRIERS
const e = strings.CSV_EXPORT
const s = strings.SUMMARY_REPORT
const p = strings.PDF_EXPORT

interface ICarrierSummaryReport {
  print?: boolean
  carrierInfoRef?: React.RefObject<HTMLDivElement>
  carrierInfoLoading?: boolean
}

const CarrierSummaryReport: React.FC<ICarrierSummaryReport> = (props) => {
  const { print, carrierInfoRef, carrierInfoLoading = false } = props
  const carrierName = localStorage.getItem('carrierName') || ''
  const [loading, setLoading] = useState(false)
  const [showExportPdfButton, setShowExportPdfButton] = useState(true)
  const apiRef = useGridApiRef()
  const [milesByStateTablePdfData, setMilesByStateTablePdfData] =
    useState<any[][] | null>(null)
  const [milesByStateTablePdfColumns, setMilesByStateTablePdfColumns] =
    useState<string[] | null>(null)
  const [milesByZipcodeTablePdfData, setMilesByZipcodeTablePdfData] =
    useState<any[][] | null>(null)
  const [milesByZipcodeTablePdfColumns, setMilesByZipcodeTablePdfColumns] =
    useState<string[] | null>(null)
  const [activeUnitsPdfData, setActiveUnitsPdfData] =
    useState<any[][] | null>(null)
  const [activeUnitsPdfColumns, setActiveUnitsPdfColumns] =
    useState<string[] | null>(null)
  const [radiusOfOperationRefs, setRadiusOfOperationRefs] = useState<
    React.RefObject<HTMLDivElement>[]
  >([])

  const [headerKpisLoading, setHeaderKpisLoading] = useState(true)
  const [estimatedMilesLoading, setEstimatedMilesLoading] = useState(true)
  const [radiusOfOperationLoading, setRadiusOfOperationLoading] = useState(true)
  const [activeUnitsLoading, setActiveUnitsLoading] = useState(true)

  // Refs for PDF export sections (non table sections)
  const headerKpisRef = useRef<HTMLDivElement>(null)
  const estimatedMilesRef = useRef<HTMLDivElement>(null)
  const radiusOfOperationRef = useRef<HTMLDivElement>(null)

  const handleRadiusOfOperationPdfDataChange = useCallback(
    (
      milesByStateData: any[][] | null,
      milesByStateColumns: string[] | null,
      milesByZipcodeData: any[][] | null,
      milesByZipcodeColumns: string[] | null
    ) => {
      setMilesByStateTablePdfData(milesByStateData)
      setMilesByStateTablePdfColumns(milesByStateColumns)
      setMilesByZipcodeTablePdfData(milesByZipcodeData)
      setMilesByZipcodeTablePdfColumns(milesByZipcodeColumns)
    },
    []
  )

  const handleActiveUnitsPdfDataChange = useCallback(
    (data: any[][] | null, columns: string[] | null) => {
      setActiveUnitsPdfData(data)
      setActiveUnitsPdfColumns(columns)
    },
    []
  )

  const handleHeaderKpisLoadingChange = useCallback((isLoading: boolean) => {
    setHeaderKpisLoading(isLoading)
  }, [])

  const handleEstimatedMilesLoadingChange = useCallback(
    (isLoading: boolean) => {
      setEstimatedMilesLoading(isLoading)
    },
    []
  )

  const handleRadiusOfOperationLoadingChange = useCallback(
    (isLoading: boolean) => {
      setRadiusOfOperationLoading(isLoading)
    },
    []
  )

  const handleActiveUnitsLoadingChange = useCallback((isLoading: boolean) => {
    setActiveUnitsLoading(isLoading)
  }, [])

  const isAnyComponentLoading = useMemo(() => {
    return (
      carrierInfoLoading ||
      headerKpisLoading ||
      estimatedMilesLoading ||
      radiusOfOperationLoading ||
      activeUnitsLoading
    )
  }, [
    carrierInfoLoading,
    headerKpisLoading,
    estimatedMilesLoading,
    radiusOfOperationLoading,
    activeUnitsLoading,
  ])

  // Build pdf export sections
  const exportSections = useMemo(() => {
    const sections: Array<PdfExportSection> = [
      {
        refs: [headerKpisRef],
        type: 'visual',
      },
      { refs: [estimatedMilesRef], type: 'visual' },
    ]

    if (radiusOfOperationRefs.length > 0) {
      sections.push({
        refs: radiusOfOperationRefs,
        type: 'visual',
        title: s.RADIUS_OF_OPERATION.RADIUS_OF_OPERATION_TITLE,
        gridColumns: 2,
      })
    }

    if (milesByStateTablePdfData && milesByStateTablePdfColumns) {
      sections.push({
        type: 'table',
        title: s.RADIUS_OF_OPERATION.MILES_BY_STATE,
        data: milesByStateTablePdfData,
        columns: milesByStateTablePdfColumns,
      })
    }

    if (milesByZipcodeTablePdfData && milesByZipcodeTablePdfColumns) {
      sections.push({
        type: 'table',
        title: s.RADIUS_OF_OPERATION.TOP_ZIPCODES_BY_ACTIVITY,
        data: milesByZipcodeTablePdfData,
        columns: milesByZipcodeTablePdfColumns,
      })
    }

    if (activeUnitsPdfData && activeUnitsPdfColumns) {
      sections.push({
        type: 'table',
        title: s.ACTIVE_UNITS.ACTIVE_UNITS_TITLE,
        data: activeUnitsPdfData,
        columns: activeUnitsPdfColumns,
      })
    }

    return sections
  }, [
    activeUnitsPdfData,
    activeUnitsPdfColumns,
    radiusOfOperationRefs,
    milesByStateTablePdfData,
    milesByStateTablePdfColumns,
    milesByZipcodeTablePdfData,
    milesByZipcodeTablePdfColumns,
  ])

  const HeaderDom = () => {
    return (
      <Container className={styles.headerContainer}>
        <Container className={styles.headerTitleContainer}>
          <TypoGraph
            variant="h1"
            sx={{ marginRight: '1rem' }}
            mb={0}
            content={'Carrier Summary Report'}
          />
          <Chip
            label="BETA"
            color="primary"
            size="small"
            sx={{
              height: '24px',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'white',
            }}
          />
        </Container>
        {showExportPdfButton && (
          <PdfExport
            sections={exportSections}
            disabled={isAnyComponentLoading}
            carrierInfoRef={carrierInfoRef}
            filename={`${(carrierName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') || 'Carrier')}_Summary_Report.pdf`}
            reportTitle={carrierName ? p.SUMMARY_REPORT.TITLE(carrierName) : p.SUMMARY_REPORT.DEFAULT_TITLE}
            disclaimer={p.DISCLAIMER}
          />
        )}
      </Container>
    )
  }

  return (
    <Container className={styles.summaryReportBladeBodyContainer}>
      <Container>
        <HeaderBar dom={<HeaderDom />} />
        {/* Header KPIs */}
        <div ref={headerKpisRef}>
          <CarrierHeaderKPIs
            onLoadingChange={handleHeaderKpisLoadingChange}
          />
        </div>
        {/* Data coverage — request earlier history */}
        {!print && <DataCoverage />}
        {/* Estimated Miles - Total */}
        <div ref={estimatedMilesRef}>
          <EstimatedMiles onLoadingChange={handleEstimatedMilesLoadingChange} />
        </div>
        {/* Radius of Operation */}
        <div ref={radiusOfOperationRef}>
          <SummaryRadiusOfOperation
            onPdfRefsChange={setRadiusOfOperationRefs}
            onPdfDataChange={handleRadiusOfOperationPdfDataChange}
            onLoadingChange={handleRadiusOfOperationLoadingChange}
          />
        </div>
        {/* Recent Crashes - Last 90 Days */}
        <></>
        {/* Speeding Analysis */}
        <></>
        {/* Safety Analysis */}
        <></>
        {/* Highest Rated Units (Best Performers) */}
        <></>
        {/* Time-Based Analysis */}
        <></>
        {/* New Unit Added - Last 90 Days */}
        <></>
        {/* Active Units - Past 90 Days */}

        <ActiveUnits
          onPdfDataChange={handleActiveUnitsPdfDataChange}
          onLoadingChange={handleActiveUnitsLoadingChange}
        />

        {/* Inactive Vehicles */}
        <></>
      </Container>

      <ScrollToTopCornerButton />
    </Container>
  )
}

export default CarrierSummaryReport
